// client/src/pages/Dashboard.tsx
import { useState, useEffect, useCallback, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { Link, useNavigate } from 'react-router-dom';
import {
    TrendingUp,
    ClipboardList,
    Store,
    Users,
    CheckCircle2,
    Clock,
    AlertCircle,
    Eye,
    Pencil,
    Image,
    RefreshCw,
    FileDown,
    FileSpreadsheet,
    Bell,
    Search,
    Filter,
    ArrowUp,
    ArrowDown
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { getJobCards } from '../services/jobcardApi';
import { getStores } from '../services/storeApi';
import { getUsers } from '../services/userApi';
import { JobCard } from '../types/jobcard';
import { AxiosResponse, AxiosError } from 'axios';
import api from '../services/api';
import { showSuccessToast, showErrorToast } from '../utils/toast';
import SystemHealthIndicator, { updateSyncStatus } from '../components/SystemHealthIndicator';
import notificationService from '../services/notificationService';
import { getManagerStats, TechnicianPerformance, DateRange, getDashboardOverview, OverviewResponse } from '../services/dashboardApi';

type JobCardsResponse = AxiosResponse<{
    success: boolean;
    data: JobCard[] | {
        data: JobCard[];
        current_page?: number;
        last_page?: number;
        total?: number;
    };
}>;

const FILTER_STORAGE_KEY = 'jobCardsFilters';
const DEPT_OVERVIEW_FILTERS_KEY = 'deptOverviewFilters';

interface ManagerStats {
    technicianPerformance: TechnicianPerformance[];
}

export default function Dashboard() {
    const navigate = useNavigate();
    const { user } = useSelector((state: RootState) => state.auth);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [stats, setStats] = useState({
        totalJobCards: 0,
        openJobCards: 0,
        closedJobCards: 0,
        totalStores: 0,
        totalUsers: 0,
        recentJobCards: [] as JobCard[]
    });
    const [managerStats, setManagerStats] = useState<ManagerStats | null>(null);
    const [loadingManagerStats, setLoadingManagerStats] = useState(false);
  // Job Cards Overview: local UI state and helpers
  // isAdmin flag derived from current user for conditional rendering of admin-only sections
  const isAdmin = user?.utype === 'ADMIN';
  // Sorting state for per-store table; key controls the column, dir controls ascending/descending
  const [storeSortKey, setStoreSortKey] = useState<'name' | 'open' | 'closed' | 'total' | 'pct'>('total');
  const [storeSortDir, setStoreSortDir] = useState<'asc' | 'desc'>('desc');
  // Regional/Area coaches selection and accordion open state (data to be wired to backend later)
  const [isRegionalCoachesOpen, setIsRegionalCoachesOpen] = useState<boolean>(true);
  const [isAreaCoachesOpen, setIsAreaCoachesOpen] = useState<boolean>(true);
  const [selectedRegionalCoach, setSelectedRegionalCoach] = useState<{ id: number; name: string } | null>(null);
  const [selectedAreaCoach, setSelectedAreaCoach] = useState<{ id: number; name: string } | null>(null);
  // Loading/error flags for the overview data block
  const [jcLoading, setJcLoading] = useState<boolean>(false);
  const [jcError, setJcError] = useState<string | null>(null);
  // Overview datasets; seeded with safe defaults and designed to be hydrated from server in a future enhancement
  const [jcSummary, setJcSummary] = useState<{ total: number; open: number; closed: number } | null>(null);
  const [jcRegionalCoaches, setJcRegionalCoaches] = useState<Array<{ id: number; name: string; summary: { total: number; open: number; closed: number } }>>([]);
  const [jcAreaCoaches, setJcAreaCoaches] = useState<Array<{ id: number; name: string; summary: { total: number; open: number; closed: number } }>>([]);
  const [jcStores, setJcStores] = useState<Array<{ id: number; name: string; storenumber?: string; open: number; closed: number; total: number }>>([]);

  // Helper: derive completion percentage (rounded to nearest integer) with guard against division by zero
  const getCompletionPct = (closed: number, total: number): number => {
    if (!total || total <= 0) return 0;
    return Math.round((closed / total) * 100);
  };

  // Helper: export per-store rows to CSV. This is kept local to the page for UX convenience.
  const exportStoresToCsv = (rows: Array<{ id: number; name: string; storenumber?: string; open: number; closed: number; total: number }>): void => {
    try {
      const headers = ['Store', 'Open', 'Closed', 'Total', 'Completion %'];
      const csvLines = [headers.join(',')];
      rows.forEach(r => {
        const storeName = `${r.name}${r.storenumber ? ` - ${r.storenumber}` : ''}`;
        const pct = getCompletionPct(r.closed, r.total);
        const values = [storeName, String(r.open), String(r.closed), String(r.total), String(pct)];
        const escaped = values.map(v => {
          const safe = v.replace(/"/g, '""');
          return /[",\n]/.test(safe) ? `"${safe}"` : safe;
        });
        csvLines.push(escaped.join(','));
      });
      const csvContent = '\uFEFF' + csvLines.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `store_job_cards_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      showErrorToast('Failed to export CSV');
    }
  };

  // Navigate to Job Cards filtered by the user who matches a store's email.
  // Resolves email -> user_id via users service, then preserves the current date range in URL params.
  const navigateToStoreUserJobCards = async (storeEmail?: string, storeId?: number) => {
    try {
      if (!storeEmail) {
        showErrorToast('No store email found');
        return;
      }
      const usersRes = await getUsers();
      const allUsers = usersRes.data || [];
      const matched = allUsers.find((u: { id: number; email?: string }) => u.email && u.email.toLowerCase() === storeEmail.toLowerCase());
      // Build date range from selection
      let actualStartDate = '';
      let actualEndDate = '';
      if (useCustomRange && customDateRange.startDate && customDateRange.endDate) {
        actualStartDate = customDateRange.startDate;
        actualEndDate = customDateRange.endDate;
      } else {
        const dr = getDateRangeFromPredefined(dateRange);
        actualStartDate = dr.startDate;
        actualEndDate = dr.endDate;
      }

      // If no user matched by email, fall back to store filter so the user still gets a useful view
      if (!matched) {
        const filtersFallback = {
          searchTerm: '',
          statusFilter: 'all' as const,
          dateRangeFilter: { startDate: actualStartDate, endDate: actualEndDate },
          storeFilter: storeId ?? 'all',
          userFilter: 'all',
          departmentFilter: 'all'
        };
        localStorage.setItem('jobCardsFilters', JSON.stringify(filtersFallback));
        navigate('/jobcards');
        showErrorToast('No matching user for store email; showing store-filtered job cards');
        return;
      }

      // Prepare URL params and localStorage filters, similar to technician view navigation
      const params: string[] = [];
      if (actualStartDate && actualEndDate) {
        params.push(`start_date=${actualStartDate}`, `end_date=${actualEndDate}`);
      }
      params.push(`user_id=${matched.id}`);
      const dateRangeParams = params.length ? `?${params.join('&')}` : '';

      const filters = {
        searchTerm: '',
        statusFilter: 'all' as const,
        dateRangeFilter: { startDate: actualStartDate, endDate: actualEndDate },
        storeFilter: 'all',
        userFilter: String(matched.id),
        departmentFilter: 'all'
      };
      localStorage.setItem('jobCardsFilters', JSON.stringify(filters));

      navigate(`/jobcards${dateRangeParams}`);
    } catch {
      showErrorToast('Failed to navigate to store job cards');
    }
  };
    // Load persisted filters from localStorage
    const loadPersistedFilters = () => {
        try {
            const saved = localStorage.getItem(DEPT_OVERVIEW_FILTERS_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    statusFilter: parsed.statusFilter || 'all',
                    performanceRange: parsed.performanceRange || 'all',
                    field: parsed.field || 'completion_percentage',
                    direction: parsed.direction || 'desc',
                    dateRange: parsed.dateRange || 'last_3_months',
                    useCustomRange: parsed.useCustomRange || false,
                    customDateRange: parsed.customDateRange || { startDate: '', endDate: '' }
                };
            }
        } catch (error) {
            console.warn('Failed to load persisted department overview filters:', error);
        }
        return {
            statusFilter: 'all',
            performanceRange: 'all',
            field: 'completion_percentage',
            direction: 'desc',
            dateRange: 'last_3_months',
            useCustomRange: false,
            customDateRange: { startDate: '', endDate: '' }
        };
    };

    // Save filters to localStorage (excluding search term)
    const saveFiltersToStorage = (filters: typeof deptOverviewFilters, sorting: typeof deptOverviewSorting, dateRangeData?: { dateRange: DateRange, useCustomRange: boolean, customDateRange: { startDate: string, endDate: string } }) => {
        try {
            const toSave = {
                statusFilter: filters.statusFilter,
                performanceRange: filters.performanceRange,
                field: sorting.field,
                direction: sorting.direction,
                ...(dateRangeData && {
                    dateRange: dateRangeData.dateRange,
                    useCustomRange: dateRangeData.useCustomRange,
                    customDateRange: dateRangeData.customDateRange
                })
            };
            localStorage.setItem(DEPT_OVERVIEW_FILTERS_KEY, JSON.stringify(toSave));
        } catch (error) {
            console.warn('Failed to save department overview filters:', error);
        }
    };

    // Initialize state with persisted values
    const persistedFilters = loadPersistedFilters();
    
    // Update date range state with persisted values
    const [dateRange, setDateRangeState] = useState<DateRange>(persistedFilters.dateRange);
    const [customDateRange, setCustomDateRangeState] = useState(persistedFilters.customDateRange);
    const [useCustomRange, setUseCustomRangeState] = useState(persistedFilters.useCustomRange);
    
    // Department Overview filters and sorting state
    const [deptOverviewFilters, setDeptOverviewFiltersState] = useState({
        searchTerm: '', // Never persisted - always starts empty
        statusFilter: persistedFilters.statusFilter,
        performanceRange: persistedFilters.performanceRange
    });
    const [deptOverviewSorting, setDeptOverviewSortingState] = useState({
        field: persistedFilters.field,
        direction: persistedFilters.direction
    });

    // Wrapper functions that update state and persist to localStorage
    const setDeptOverviewFilters = (updater: React.SetStateAction<typeof deptOverviewFilters>) => {
        setDeptOverviewFiltersState(prev => {
            const newFilters = typeof updater === 'function' ? updater(prev) : updater;
            // Save to localStorage (excluding search term)
            saveFiltersToStorage(newFilters, deptOverviewSorting);
            return newFilters;
        });
    };

    const setDeptOverviewSorting = (updater: React.SetStateAction<typeof deptOverviewSorting>) => {
        setDeptOverviewSortingState(prev => {
            const newSorting = typeof updater === 'function' ? updater(prev) : updater;
            // Save to localStorage
            saveFiltersToStorage(deptOverviewFilters, newSorting);
            return newSorting;
        });
    };

    // Wrapper functions for date range that also persist to localStorage
    const setDateRange = (updater: React.SetStateAction<DateRange>) => {
        setDateRangeState(prev => {
            const newDateRange = typeof updater === 'function' ? updater(prev) : updater;
            // Save to localStorage
            saveFiltersToStorage(deptOverviewFilters, deptOverviewSorting, {
                dateRange: newDateRange,
                useCustomRange,
                customDateRange
            });
            return newDateRange;
        });
    };

    const setCustomDateRange = (updater: React.SetStateAction<{ startDate: string, endDate: string }>) => {
        setCustomDateRangeState((prev: { startDate: string, endDate: string }) => {
            const newCustomDateRange = typeof updater === 'function' ? updater(prev) : updater;
            // Save to localStorage
            saveFiltersToStorage(deptOverviewFilters, deptOverviewSorting, {
                dateRange,
                useCustomRange,
                customDateRange: newCustomDateRange
            });
            return newCustomDateRange;
        });
    };

    const setUseCustomRange = (updater: React.SetStateAction<boolean>) => {
        setUseCustomRangeState((prev: boolean) => {
            const newUseCustomRange = typeof updater === 'function' ? updater(prev) : updater;
            // Save to localStorage
            saveFiltersToStorage(deptOverviewFilters, deptOverviewSorting, {
                dateRange,
                useCustomRange: newUseCustomRange,
                customDateRange
            });
            return newUseCustomRange;
        });
    };

    // Export helpers for manager leaderboard
    // These functions generate CSV and Excel files from the current managerStats. The
    // data includes each technician's name, open/closed/total counts, and completion percentage.
    const generateLeaderboardRows = () => {
        // Guard: only proceed if stats are present
        if (!managerStats) return [] as Array<Record<string, string | number>>;

        return getFilteredAndSortedTechnicians().map(tech => {
            const total = tech.open + tech.closed;
            const completionPct = total > 0 ? Number(((tech.closed / total) * 100).toFixed(1)) : 0;
            return {
                Name: tech.user.name,
                Open: tech.open,
                Closed: tech.closed,
                Total: total,
                'Completion %': completionPct,
            };
        });
    };

    const getLeaderboardFilename = (ext: 'csv' | 'xlsx') => {
        const department = user?.position?.department?.name || 'Department';
        const date = new Date().toISOString().slice(0, 10);
        
        // Create a descriptive date range string for the filename
        let dateRangeString: string = dateRange;
        if (useCustomRange && customDateRange.startDate && customDateRange.endDate) {
            const startDate = new Date(customDateRange.startDate).toISOString().slice(0, 10);
            const endDate = new Date(customDateRange.endDate).toISOString().slice(0, 10);
            dateRangeString = `custom_${startDate}_to_${endDate}`;
        }
        
        return `${department.replace(/\s+/g, '_')}_leaderboard_${dateRangeString}_${date}.${ext}`;
    };

    const exportLeaderboardCSV = () => {
        // Build CSV content with a UTF-8 BOM for Excel compatibility on Windows
        const rows = generateLeaderboardRows();
        const headers = ['Name', 'Open', 'Closed', 'Total', 'Completion %'];
        const csvLines = [headers.join(',')];
        rows.forEach(row => {
            const line = headers.map(h => {
                const value = String(row[h as keyof typeof row] ?? '');
                // Escape commas, quotes, and newlines per RFC 4180
                const escaped = value.replace(/"/g, '""');
                return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
            }).join(',');
            csvLines.push(line);
        });
        const csvContent = '\uFEFF' + csvLines.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = getLeaderboardFilename('csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const exportLeaderboardExcel = () => {
        // Create an XLSX workbook from the same rows
        const rows = generateLeaderboardRows();
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Leaderboard');
        XLSX.writeFile(workbook, getLeaderboardFilename('xlsx'));
    };

    // Fetch dashboard data with accurate counts
    // Note: We make separate API calls for total, open, and closed job cards to get precise counts
    // instead of using ratio calculations which can be inaccurate due to:
    // 1. Pagination limits (e.g., requesting 1000 but getting only a subset)
    // 2. Non-uniform data distribution across pages
    // 3. User permission filtering (non-admin users only see their own job cards)
    // This ensures dashboard counts match the job cards page filtered results
    const fetchDashboardData = useCallback(async () => {
        try {
            setLoading(true);

            // Check if user is a manager and get their department's user IDs
            const isManager = user?.position?.name === 'Manager';
            let departmentUserIds: number[] | undefined = undefined;

            if (isManager) {
                // Initial fetch for manager stats on component load
                // fetchManagerStats(); // Moved to useEffect
                
                const allUsersRes = await getUsers();
                const allUsers = allUsersRes.data || [];
                const departmentUsers = allUsers.filter(u => u.position?.department?.id === user.position?.department?.id);
                departmentUserIds = departmentUsers.map(u => u.id);

                // If no users found in department, default to manager's own ID
                if (departmentUserIds.length === 0 && user.id) {
                    departmentUserIds = [user.id];
                }
            }

            // Get accurate counts by making separate calls for each status
            const [allJobCardsRes, openJobCardsRes, closedJobCardsRes, recentJobCardsRes, storesRes, usersRes] = await Promise.all([
                getJobCards('', undefined, 1, { user_id: departmentUserIds }) as Promise<JobCardsResponse>,  // Get total count (just need pagination info)
                getJobCards('', false, 1, { user_id: departmentUserIds }) as Promise<JobCardsResponse>,      // Get open job cards count
                getJobCards('', true, 1, { user_id: departmentUserIds }) as Promise<JobCardsResponse>,       // Get closed job cards count
                getJobCards('', undefined, 5, { user_id: departmentUserIds }) as Promise<JobCardsResponse>,  // Get 5 recent ones for display
                getStores(),
                getUsers()
            ]);

            // Extract accurate counts from pagination data
            let totalJobCards = 0;
            let totalOpenJobCards = 0;
            let totalClosedJobCards = 0;

            // Get total count
            const allJobCardsData = allJobCardsRes.data.data;
            if (allJobCardsData && typeof allJobCardsData === 'object' && 'total' in allJobCardsData) {
                totalJobCards = allJobCardsData.total || 0;
            } else if (Array.isArray(allJobCardsData)) {
                totalJobCards = allJobCardsData.length;
            }

            // Get open count
            const openJobCardsData = openJobCardsRes.data.data;
            if (openJobCardsData && typeof openJobCardsData === 'object' && 'total' in openJobCardsData) {
                totalOpenJobCards = openJobCardsData.total || 0;
            } else if (Array.isArray(openJobCardsData)) {
                totalOpenJobCards = openJobCardsData.length;
            }

            // Get closed count
            const closedJobCardsData = closedJobCardsRes.data.data;
            if (closedJobCardsData && typeof closedJobCardsData === 'object' && 'total' in closedJobCardsData) {
                totalClosedJobCards = closedJobCardsData.total || 0;
            } else if (Array.isArray(closedJobCardsData)) {
                totalClosedJobCards = closedJobCardsData.length;
            }

            // Process recent job cards for display
            const recentJobCardsData = recentJobCardsRes.data.data;
            let recentJobCards: JobCard[] = [];
            
            if (recentJobCardsData && typeof recentJobCardsData === 'object' && 'data' in recentJobCardsData) {
                recentJobCards = recentJobCardsData.data;
            } else if (Array.isArray(recentJobCardsData)) {
                recentJobCards = recentJobCardsData;
            }

            const stores = storesRes.data?.data || [];
            const users = usersRes.data || [];

            setStats(prevStats => ({
                ...prevStats,
                totalJobCards: totalJobCards,
                openJobCards: totalOpenJobCards,
                closedJobCards: totalClosedJobCards,
                totalStores: stores.length,
                totalUsers: users.length,
                recentJobCards: recentJobCards
            }));

        } catch {
            // Silently handle errors
        } finally {
            setLoading(false);
        }
    }, [user]);

    const fetchManagerStats = useCallback(async () => {
        if (!user || user.position?.name !== 'Manager' || !user.position.department?.id) {
            return;
        }

        setLoadingManagerStats(true);
        try {
            const allUsersRes = await getUsers();
            const departmentUsers = allUsersRes.data.filter(u => u.position?.department?.id === user.position?.department?.id);
            const departmentUserIds = departmentUsers.map(u => u.id);

            if (departmentUserIds.length > 0) {
                // Use custom date range if enabled and both dates are provided
                const dateRangeToUse = useCustomRange && customDateRange.startDate && customDateRange.endDate 
                    ? { start_date: customDateRange.startDate, end_date: customDateRange.endDate }
                    : dateRange;

                const response = await getManagerStats(departmentUserIds, dateRangeToUse);
                setManagerStats({
                    technicianPerformance: response.data.technician_performance,
                });
            }
        } catch {
            showErrorToast("Failed to load manager statistics");
        } finally {
            setLoadingManagerStats(false);
        }
    }, [user, dateRange, customDateRange, useCustomRange]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    useEffect(() => {
        // Refetch manager stats when date range or custom date range changes
        if (user?.position?.name === 'Manager') {
            fetchManagerStats();
        }
    }, [dateRange, useCustomRange, user, fetchManagerStats]);

  // Seed Job Cards Overview with existing dashboard stats so the section is useful immediately.
  // Future: replace with an API that returns regional/area coach rollups and per-store breakdowns.
  const fetchOverview = useCallback(async () => {
    try {
      setJcLoading(true);
      setJcError(null);

      // Build date params from current selection
      let startDate = '';
      let endDate = '';
      if (useCustomRange && customDateRange.startDate && customDateRange.endDate) {
        startDate = customDateRange.startDate;
        endDate = customDateRange.endDate;
      } else {
        const dr = getDateRangeFromPredefined(dateRange);
        startDate = dr.startDate;
        endDate = dr.endDate;
      }

      const response = await getDashboardOverview({
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        regional_id: selectedRegionalCoach?.id,
        area_id: selectedAreaCoach?.id,
      });

      const payload = (response.data as OverviewResponse).data;
      setJcSummary(payload.summary);
      setJcRegionalCoaches(payload.regional_coaches);
      setJcAreaCoaches(payload.area_coaches);
      setJcStores(payload.stores);
    } catch {
      setJcError('Failed to load overview');
      setJcSummary({ total: 0, open: 0, closed: 0 });
      setJcRegionalCoaches([]);
      setJcAreaCoaches([]);
      setJcStores([]);
    } finally {
      setJcLoading(false);
    }
  }, [dateRange, customDateRange, useCustomRange, selectedAreaCoach?.id, selectedRegionalCoach?.id]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

    const handleMigrateJobPictures = async () => {
        try {
            const response = await api.post('/admin/migrate-job-pictures');
            if (response.data.success) {
                showSuccessToast('Job pictures migration completed successfully');
            } else {
                showErrorToast('Migration failed: ' + response.data.message);
            }
        } catch (error: unknown) {
            const apiError = error as { response?: { data?: { message?: string } }, message?: string };
            showErrorToast('Migration failed: ' + (apiError.response?.data?.message || apiError.message));
        }
    };

    // Function to sync remote data with local database
    const handleSyncRemoteData = async () => {
        try {
            setSyncing(true);
            const response = await api.post('/system/sync-all');
            if (response.data.success) {
                showSuccessToast('Remote data synchronized successfully');
                // Update sync status in localStorage
                updateSyncStatus(true);
                // Refresh dashboard data to show updated counts
                await fetchDashboardData();
            } else {
                showErrorToast('Sync completed with errors. Check server logs for details.');
                updateSyncStatus(false);
            }
        } catch (error) {
            const axiosError = error as AxiosError;
            const errorMessage = axiosError.response?.data && typeof axiosError.response.data === 'object' && 'message' in axiosError.response.data 
                ? axiosError.response.data.message as string 
                : axiosError.message;
            showErrorToast('Sync failed: ' + errorMessage);
            updateSyncStatus(false);
        } finally {
            setSyncing(false);
        }
    };

    // Function to navigate to job cards with specific filters
    const navigateToJobCards = (statusFilter: 'all' | 'open' | 'closed' = 'all') => {
        // Set filter values in localStorage
        const isManager = user?.position?.name === 'Manager';
        let userFilter: string | number[] = 'all';

        if (isManager && user?.position?.department?.id) {
            // This part is tricky because we don't have the full user list here.
            // A better approach might be to just navigate and let the jobcards page handle the filtering.
            // For now, we will clear user filters for managers to avoid incorrect data.
            userFilter = 'all'; 
        }

        const filters = {
            searchTerm: '',
            statusFilter,
            dateRangeFilter: {
                startDate: '',
                endDate: ''
            },
            storeFilter: 'all',
            userFilter: userFilter,
        };
        
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
        navigate('/jobcards');
    };

    // Function to convert predefined date range to actual dates
    const getDateRangeFromPredefined = (range: DateRange) => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        switch (range) {
            case 'this_week': {
                const startOfWeek = new Date(today);
                startOfWeek.setDate(today.getDate() - today.getDay());
                return {
                    startDate: startOfWeek.toISOString().split('T')[0],
                    endDate: today.toISOString().split('T')[0]
                };
            }
            case 'this_month':
                return {
                    startDate: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0],
                    endDate: today.toISOString().split('T')[0]
                };
            case 'last_3_months': {
                const threeMonthsAgo = new Date(today);
                threeMonthsAgo.setMonth(today.getMonth() - 3);
                return {
                    startDate: threeMonthsAgo.toISOString().split('T')[0],
                    endDate: today.toISOString().split('T')[0]
                };
            }
            case 'last_6_months': {
                const sixMonthsAgo = new Date(today);
                sixMonthsAgo.setMonth(today.getMonth() - 6);
                return {
                    startDate: sixMonthsAgo.toISOString().split('T')[0],
                    endDate: today.toISOString().split('T')[0]
                };
            }
            case 'this_year':
                return {
                    startDate: new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0],
                    endDate: today.toISOString().split('T')[0]
                };
            case 'all_time':
                return {
                    startDate: '',
                    endDate: ''
                };
            default:
                return {
                    startDate: '',
                    endDate: ''
                };
        }
    };

    // Function to navigate to job cards with technician-specific filters
    const navigateToTechnicianJobCards = (technicianId: number) => {
        // Get actual start and end dates
        let actualStartDate = '';
        let actualEndDate = '';
        
        if (useCustomRange && customDateRange.startDate && customDateRange.endDate) {
            actualStartDate = customDateRange.startDate;
            actualEndDate = customDateRange.endDate;
        } else {
            const dateRangeResult = getDateRangeFromPredefined(dateRange);
            actualStartDate = dateRangeResult.startDate;
            actualEndDate = dateRangeResult.endDate;
        }

        // Prepare date range for URL parameters
        let dateRangeParams = '';
        if (actualStartDate && actualEndDate) {
            dateRangeParams = `?start_date=${actualStartDate}&end_date=${actualEndDate}&user_id=${technicianId}`;
        } else {
            dateRangeParams = `?user_id=${technicianId}`;
        }

        // Set filter values in localStorage with technician and date range
        const filters = {
            searchTerm: '',
            statusFilter: 'all' as const,
            dateRangeFilter: {
                startDate: actualStartDate,
                endDate: actualEndDate
            },
            storeFilter: 'all',
            userFilter: technicianId.toString(),
            departmentFilter: 'all'
        };
        
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
        navigate(`/jobcards${dateRangeParams}`);
    };

    // Filter and sort technician performance data
    const getFilteredAndSortedTechnicians = useCallback(() => {
        if (!managerStats?.technicianPerformance) return [];
        
        let filtered = [...managerStats.technicianPerformance];
        
        // Apply search filter
        if (deptOverviewFilters.searchTerm) {
            const searchLower = deptOverviewFilters.searchTerm.toLowerCase();
            filtered = filtered.filter(tech => 
                tech.user.name.toLowerCase().includes(searchLower)
            );
        }
        
        // Apply status filter
        if (deptOverviewFilters.statusFilter !== 'all') {
            filtered = filtered.filter(tech => {
                const total = tech.open + tech.closed;
                const completionPercentage = total > 0 ? (tech.closed / total) * 100 : 0;
                
                switch (deptOverviewFilters.statusFilter) {
                    case 'high_performers':
                        return completionPercentage >= 80;
                    case 'low_performers':
                        return completionPercentage < 50;
                    case 'open_heavy':
                        return tech.open > tech.closed;
                    case 'closed_heavy':
                        return tech.closed > tech.open;
                    default:
                        return true;
                }
            });
        }
        
        // Apply performance range filter
        if (deptOverviewFilters.performanceRange !== 'all') {
            // First sort by completion percentage to determine ranges
            const sortedByCompletion = [...filtered].sort((a, b) => {
                const aCompletion = a.open + a.closed > 0 ? (a.closed / (a.open + a.closed)) * 100 : 0;
                const bCompletion = b.open + b.closed > 0 ? (b.closed / (b.open + b.closed)) * 100 : 0;
                return bCompletion - aCompletion;
            });
            
            const total = sortedByCompletion.length;
            const top25Count = Math.ceil(total * 0.25);
            const bottom25Count = Math.floor(total * 0.25);
            
            switch (deptOverviewFilters.performanceRange) {
                case 'top_25':
                    filtered = sortedByCompletion.slice(0, top25Count);
                    break;
                case 'bottom_25':
                    filtered = sortedByCompletion.slice(-bottom25Count);
                    break;
                case 'middle_50':
                    filtered = sortedByCompletion.slice(top25Count, total - bottom25Count);
                    break;
            }
        }
        
        // Apply sorting
        filtered.sort((a, b) => {
            let aValue: number | string;
            let bValue: number | string;
            
            switch (deptOverviewSorting.field) {
                case 'name':
                    aValue = a.user.name.toLowerCase();
                    bValue = b.user.name.toLowerCase();
                    break;
                case 'completion_percentage':
                    aValue = a.open + a.closed > 0 ? (a.closed / (a.open + a.closed)) * 100 : 0;
                    bValue = b.open + b.closed > 0 ? (b.closed / (b.open + b.closed)) * 100 : 0;
                    break;
                case 'total_jobs':
                    aValue = a.open + a.closed;
                    bValue = b.open + b.closed;
                    break;
                case 'open_jobs':
                    aValue = a.open;
                    bValue = b.open;
                    break;
                case 'closed_jobs':
                    aValue = a.closed;
                    bValue = b.closed;
                    break;
                default:
                    aValue = 0;
                    bValue = 0;
            }
            
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return deptOverviewSorting.direction === 'asc' 
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            } else {
                return deptOverviewSorting.direction === 'asc' 
                    ? (aValue as number) - (bValue as number)
                    : (bValue as number) - (aValue as number);
            }
        });
        
        return filtered;
    }, [managerStats, deptOverviewFilters, deptOverviewSorting]);

    // Function to test push notifications
    const testNotification = async () => {
        try {
            // Check if notifications are supported
            if (!('Notification' in window)) {
                showErrorToast('This browser does not support notifications');
                return;
            }

            // Check permission status
            const permissionStatus = notificationService.getPermissionStatus();
            if (!permissionStatus.granted) {
                showErrorToast('Notification permission not granted. Please enable notifications first.');
                return;
            }

            // Send a test notification
            await notificationService.sendPushNotification({
                title: 'Test Notification',
                body: 'This is a test notification from the Job Card System!',
                icon: '/icons/icon-192x192.png',
                tag: 'test-notification',
                data: {
                    type: 'test',
                    timestamp: Date.now()
                }
            });

            showSuccessToast('Test notification sent successfully!');
        } catch (error) {
            console.error('Failed to send test notification:', error);
            showErrorToast('Failed to send test notification. Check console for details.');
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
                <div className="w-12 h-12 border-4 border-gray-200 dark:border-gray-700 border-t-blue-500 dark:border-t-blue-400 rounded-full animate-spin mb-4"></div>
                <div className="text-gray-600 dark:text-gray-400">Loading dashboard data...</div>
                <div className="text-sm text-gray-500 dark:text-gray-500 mt-2">Fetching job card statistics and recent activity</div>
            </div>
        );
    }

    const completionRate = stats.totalJobCards > 0
        ? ((stats.closedJobCards / stats.totalJobCards) * 100).toFixed(1)
        : 0;

    return (
        <>
            <header className="mb-8">
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
                        <p className="text-gray-600 dark:text-gray-400">Welcome back, {user?.name}</p>
                    </div>
                    
                    {user?.utype === 'ADMIN' && (
                        <SystemHealthIndicator showDetails={true} className="bg-white dark:bg-gray-800" />
                    )}
                </div>
                
                {/* Test Notification Button - moved to its own row */}
                <div className="flex justify-start">
                    <button
                        onClick={testNotification}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                        title="Send a test notification"
                    >
                        <Bell className="w-4 h-4" />
                        Test Notification
                    </button>
                </div>
            </header>

            {/* Notification Permission Banner */}
            {!notificationService.hasPermission() && notificationService.isNotificationSupported() && (
                <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <Bell className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-3" />
                            <div>
                                <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                                    Enable Notifications
                                </h3>
                                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                                    Stay updated with job card notifications and reminders.
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate('/settings')}
                            className="px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white text-sm rounded-md transition-colors duration-200"
                        >
                            Enable
                        </button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <DashboardCard
                    title="Total Job Cards"
                    value={stats.totalJobCards.toString()}
                    icon={<ClipboardList className="w-6 h-6 text-blue-600 dark:text-blue-400" />}
                    footer={`${completionRate}% completion rate`}
                    variant="blue"
                    onClick={() => navigateToJobCards('all')}
                />
                <DashboardCard
                    title="Open Job Cards"
                    value={stats.openJobCards.toString()}
                    icon={<Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />}
                    footer="Require attention"
                    variant="yellow"
                    onClick={() => navigateToJobCards('open')}
                />
                <DashboardCard
                    title="Closed Job Cards"
                    value={stats.closedJobCards.toString()}
                    icon={<CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />}
                    footer="Completed jobs"
                    variant="green"
                    onClick={() => navigateToJobCards('closed')}
                />
                {user?.utype === 'ADMIN' && (
                    <>
                        <DashboardCard
                            title="Total Stores"
                            value={stats.totalStores.toString()}
                            icon={<Store className="w-6 h-6 text-green-600 dark:text-green-400" />}
                            footer="Active locations"
                            variant="green"
                            onClick={() => navigate('/stores')}
                        />
                        <DashboardCard
                            title="Total Users"
                            value={stats.totalUsers.toString()}
                            icon={<Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />}
                            footer="System users"
                            variant="purple"
                            onClick={() => navigate('/users')}
                        />
                    </>
                )}
            </div>

            {user?.position?.name === 'Manager' && managerStats && (
                <div className="mb-8 p-6 bg-white dark:bg-gray-800 rounded-lg shadow">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                        {user.position.department?.name} Overview
                    </h2>
                    <div className="mb-6 space-y-4">
                        {/* Date Range and Filters Row */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Predefined Date Range Selector */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Date Range
                                </label>
                                <div className="flex items-center gap-4">
                                    <select
                                        value={useCustomRange ? 'custom' : dateRange}
                                        onChange={(e) => {
                                            if (e.target.value === 'custom') {
                                                setUseCustomRange(true);
                                            } else {
                                                setUseCustomRange(false);
                                                setDateRange(e.target.value as DateRange);
                                            }
                                        }}
                                        className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md shadow-sm px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="this_week">This Week</option>
                                        <option value="this_month">This Month</option>
                                        <option value="last_3_months">Last 3 Months</option>
                                        <option value="last_6_months">Last 6 Months</option>
                                        <option value="this_year">This Year</option>
                                        <option value="all_time">All Time</option>
                                        <option value="custom">Custom Range</option>
                                    </select>
                                </div>
                            </div>

                            {/* Search Filter */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Search Technicians
                                </label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search by name..."
                                        value={deptOverviewFilters.searchTerm}
                                        onChange={(e) => setDeptOverviewFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                                        className="w-full pl-10 pr-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Advanced Filters Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Status Filter */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Performance Status
                                </label>
                                <select
                                    value={deptOverviewFilters.statusFilter}
                                    onChange={(e) => setDeptOverviewFilters(prev => ({ ...prev, statusFilter: e.target.value }))}
                                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md shadow-sm px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    <option value="all">All Technicians</option>
                                    <option value="high_performers">High Performers (≥80%)</option>
                                    <option value="low_performers">Low Performers (&lt;50%)</option>
                                    <option value="open_heavy">Open Heavy (More Open Jobs)</option>
                                    <option value="closed_heavy">Closed Heavy (More Closed Jobs)</option>
                                </select>
                            </div>

                            {/* Performance Range Filter */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Performance Range
                                </label>
                                <select
                                    value={deptOverviewFilters.performanceRange}
                                    onChange={(e) => setDeptOverviewFilters(prev => ({ ...prev, performanceRange: e.target.value }))}
                                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md shadow-sm px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    <option value="all">All Ranges</option>
                                    <option value="top_25">Top 25%</option>
                                    <option value="middle_50">Middle 50%</option>
                                    <option value="bottom_25">Bottom 25%</option>
                                </select>
                            </div>

                            {/* Sort Controls */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Sort By
                                </label>
                                <div className="flex gap-2">
                                    <select
                                        value={deptOverviewSorting.field}
                                        onChange={(e) => setDeptOverviewSorting(prev => ({ ...prev, field: e.target.value }))}
                                        className="flex-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md shadow-sm px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="completion_percentage">Completion %</option>
                                        <option value="name">Name</option>
                                        <option value="total_jobs">Total Jobs</option>
                                        <option value="open_jobs">Open Jobs</option>
                                        <option value="closed_jobs">Closed Jobs</option>
                                    </select>
                                    <button
                                        onClick={() => setDeptOverviewSorting(prev => ({ 
                                            ...prev, 
                                            direction: prev.direction === 'asc' ? 'desc' : 'asc' 
                                        }))}
                                        className="px-3 py-1.5 bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
                                        title={`Sort ${deptOverviewSorting.direction === 'asc' ? 'Descending' : 'Ascending'}`}
                                    >
                                        {deptOverviewSorting.direction === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Filter Summary and Clear Button */}
                        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                                <span className="font-medium">Active Filters:</span>
                                {deptOverviewFilters.searchTerm && (
                                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 rounded-full text-xs">
                                        Search: "{deptOverviewFilters.searchTerm}"
                                    </span>
                                )}
                                {deptOverviewFilters.statusFilter !== 'all' && (
                                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded-full text-xs">
                                        {deptOverviewFilters.statusFilter.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                    </span>
                                )}
                                {deptOverviewFilters.performanceRange !== 'all' && (
                                    <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 rounded-full text-xs">
                                        {deptOverviewFilters.performanceRange.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                    </span>
                                )}
                                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-full text-xs">
                                    Sort: {deptOverviewSorting.field.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())} ({deptOverviewSorting.direction === 'asc' ? 'A-Z' : 'Z-A'})
                                </span>
                                <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-300 rounded-full text-xs">
                                    Date: {useCustomRange ? 'Custom' : dateRange.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                </span>
                            </div>
                            <button
                                onClick={() => {
                                    // Clear localStorage
                                    localStorage.removeItem(DEPT_OVERVIEW_FILTERS_KEY);
                                    
                                    // Reset to default values
                                    setDeptOverviewFiltersState({
                                        searchTerm: '',
                                        statusFilter: 'all',
                                        performanceRange: 'all'
                                    });
                                    setDeptOverviewSortingState({
                                        field: 'completion_percentage',
                                        direction: 'desc'
                                    });
                                    setDateRangeState('last_3_months');
                                    setCustomDateRangeState({ startDate: '', endDate: '' });
                                    setUseCustomRangeState(false);
                                }}
                                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                            >
                                Clear All Filters
                            </button>
                        </div>

                        {/* Custom Date Range Picker */}
                        {useCustomRange && (
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                    Custom Date Range
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                            Start Date
                                        </label>
                                        <input
                                            type="date"
                                            value={customDateRange.startDate}
                                            onChange={(e) => setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                                            className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md shadow-sm px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                            End Date
                                        </label>
                                        <input
                                            type="date"
                                            value={customDateRange.endDate}
                                            onChange={(e) => setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                                            className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-md shadow-sm px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>
                                </div>
                                {customDateRange.startDate && customDateRange.endDate && (
                                    <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
                                        Showing data from {new Date(customDateRange.startDate).toLocaleDateString()} to {new Date(customDateRange.endDate).toLocaleDateString()}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {loadingManagerStats ? (
                        <div className="text-center py-8">Loading department stats...</div>
                    ) : (
                        <div className="grid grid-cols-1 gap-8">
                            <div>
                                <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-2 mb-3">
                                    <div>
                                        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">Leaderboard</h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            Showing {getFilteredAndSortedTechnicians().length} of {managerStats.technicianPerformance.length} technicians
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={exportLeaderboardCSV}
                                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                            title="Export CSV"
                                        >
                                            <FileDown className="w-4 h-4" />
                                            CSV
                                        </button>
                                        <button
                                            type="button"
                                            onClick={exportLeaderboardExcel}
                                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                            title="Export Excel"
                                        >
                                            <FileSpreadsheet className="w-4 h-4" />
                                            Excel
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                                    {getFilteredAndSortedTechnicians().length === 0 ? (
                                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                            <Filter className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                            <p className="text-lg font-medium mb-2">No technicians found</p>
                                            <p className="text-sm">Try adjusting your filters or search terms</p>
                                        </div>
                                    ) : (
                                        getFilteredAndSortedTechnicians().map(tech => {
                                        const total = tech.open + tech.closed;
                                        const closedPercentage = total > 0 ? (tech.closed / total) * 100 : 0;
                                        const openPercentage = total > 0 ? 100 - closedPercentage : 0;

                                        return (
                                            <div key={tech.user.id}>
                                                <div 
                                                    className="flex justify-between items-center text-sm mb-1 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors duration-200"
                                                    onClick={() => navigateToTechnicianJobCards(tech.user.id)}
                                                    title={`Click to view ${tech.user.name}'s job cards for the selected date range`}
                                                >
                                                    <span className="font-medium text-gray-900 dark:text-white">{tech.user.name}</span>
                                                    <div className="flex items-center gap-4">
                                                        <span className="text-yellow-600 dark:text-yellow-400">Open: {tech.open}</span>
                                                        <span className="text-green-600 dark:text-green-400">Closed: {tech.closed}</span>
                                                        <span className="font-bold text-blue-600 dark:text-blue-400">Total: {total}</span>
                                                        <span className="font-semibold text-gray-700 dark:text-gray-300 text-xs">
                                                            ({closedPercentage.toFixed(0)}% Complete)
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="w-full flex h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" title={`Open: ${openPercentage.toFixed(0)}%, Closed: ${closedPercentage.toFixed(0)}%`}>
                                                    <div className="bg-green-500" style={{ width: `${closedPercentage}%` }}></div>
                                                    <div className="bg-yellow-500" style={{ width: `${openPercentage}%` }}></div>
                                                </div>
                                            </div>
                                        );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Job Cards</h2>
                        <Link to="/jobcards" className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                            View all
                        </Link>
                    </div>
                    <div className="space-y-0 divide-y divide-gray-200 dark:divide-gray-700">
                        {stats.recentJobCards.map((jobCard, index) => (
                            <div
                                key={jobCard.id}
                                className={`flex items-center justify-between p-4 ${index % 2 === 0
                                    ? 'bg-white dark:bg-gray-800'
                                    : 'bg-gray-50 dark:bg-gray-700'
                                    } hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors`}
                            >
                                <div className="flex items-center space-x-3">
                                    {jobCard.closed ? (
                                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                                    ) : (
                                        <AlertCircle className="w-5 h-5 text-yellow-500" />
                                    )}
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                            {jobCard.job_number}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {jobCard.user?.utype === 'STORE' ? jobCard.supplier?.name : jobCard.store?.name || 'Unknown Store'}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {jobCard.user?.name || 'Unknown User'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="text-right">
                                        <p className="text-sm text-gray-900 dark:text-white">
                                            {jobCard.date ? new Date(jobCard.date).toLocaleDateString() : 'N/A'}
                                        </p>
                                        <span className={`text-xs px-2 py-1 rounded-full ${jobCard.closed
                                            ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-400'
                                            : 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-400'
                                            }`}>
                                            {jobCard.closed ? 'Closed' : 'Open'}
                                        </span>
                                        {jobCard.closed && jobCard.date_closed && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                Closed: {new Date(jobCard.date_closed).toLocaleDateString()}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center">
                                        {jobCard.closed ? (
                                            <button
                                                onClick={() => navigate(`/jobcards/view/${jobCard.id}`)}
                                                className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                                title="View Job Card"
                                            >
                                                <Eye className="w-5 h-5" />
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => navigate(`/jobcards/edit/${jobCard.id}`)}
                                                className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                                title="Edit Job Card"
                                            >
                                                <Pencil className="w-5 h-5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <Link
                            to="/jobcards"
                            className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <ClipboardList className="w-6 h-6 text-blue-500 dark:text-blue-400 mb-2" />
                            <p className="text-sm font-medium text-gray-900 dark:text-white">Job Cards</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">View and manage jobs</p>
                        </Link>
                        {user?.utype === 'ADMIN' && (
                            <>
                                <Link
                                    to="/stores"
                                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <Store className="w-6 h-6 text-green-500 dark:text-green-400 mb-2" />
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">Stores</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Manage locations</p>
                                </Link>
                                <Link
                                    to="/users"
                                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <Users className="w-6 h-6 text-purple-500 dark:text-purple-400 mb-2" />
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">Users</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Manage team members</p>
                                </Link>
                                <Link
                                    to="#"
                                    onClick={handleMigrateJobPictures}
                                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <Image className="w-6 h-6 text-orange-500 dark:text-orange-400 mb-2" />
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">Migrate Pictures</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Move job pictures to new system</p>
                                </Link>
                                <Link
                                    to="#"
                                    onClick={syncing ? undefined : handleSyncRemoteData}
                                    className={`p-4 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors ${
                                        syncing 
                                            ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' 
                                            : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                                >
                                    {syncing ? (
                                        <div className="flex flex-col items-center">
                                            <div className="w-6 h-6 border-2 border-t-cyan-500 border-cyan-500/20 rounded-full animate-spin mb-2"></div>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">Syncing...</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Please wait</p>
                                        </div>
                                    ) : (
                                        <>
                                            <RefreshCw className="w-6 h-6 text-cyan-500 dark:text-cyan-400 mb-2" />
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">Sync Remote Data</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Update stores & equipment data</p>
                                        </>
                                    )}
                                </Link>
                            </>
                        )}
                        <Link
                            to="#"
                            onClick={fetchDashboardData}
                            className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <TrendingUp className="w-6 h-6 text-indigo-500 dark:text-indigo-400 mb-2" />
                            <p className="text-sm font-medium text-gray-900 dark:text-white">Refresh Data</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Update dashboard</p>
                        </Link>
                    </div>
                </div>
            </div>
          {/* Job Cards Overview: managerial rollups and per-store breakdown.
              This block is designed to be data-source agnostic: it renders safely with seeded summary values
              and awaits future hydration for coaches and store tables. */}
          <div className="mt-8 bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6">
            <h2 className="text-xl font-medium text-gray-700 dark:text-gray-200 mb-4">Job Cards Overview</h2>
            {(() => {
              const userRoleId = (user as unknown as { user_role_id?: number } | null | undefined)?.user_role_id;
              const isRegionalCoach = userRoleId === 3;
              const jobCardSummary = jcSummary ?? { total: 0, open: 0, closed: 0 };
              const regionalCoaches: Array<{ id: number; name: string; summary: { total: number; open: number; closed: number } }> = jcRegionalCoaches;
              const areaCoaches: Array<{ id: number; name: string; summary: { total: number; open: number; closed: number } }> = jcAreaCoaches;
              const filteredStoreRows = jcStores;
              const sortedStoreRows = [...filteredStoreRows].sort((a, b) => {
                const totalA = a.open + a.closed;
                const totalB = b.open + b.closed;
                const pctA = getCompletionPct(a.closed, totalA);
                const pctB = getCompletionPct(b.closed, totalB);
                let cmp = 0;
                switch (storeSortKey) {
                  case 'name':
                    cmp = (a.name || '').localeCompare(b.name || '');
                    break;
                  case 'open':
                    cmp = a.open - b.open;
                    break;
                  case 'closed':
                    cmp = a.closed - b.closed;
                    break;
                  case 'total':
                    cmp = totalA - totalB;
                    break;
                  case 'pct':
                    cmp = pctA - pctB;
                    break;
                }
                return storeSortDir === 'asc' ? cmp : -cmp;
              });

              const onSort = (key: 'name' | 'open' | 'closed' | 'total' | 'pct') => {
                if (storeSortKey === key) {
                  setStoreSortDir(storeSortDir === 'asc' ? 'desc' : 'asc');
                } else {
                  setStoreSortKey(key);
                  setStoreSortDir('asc');
                }
              };
              const sortArrow = (key: 'name' | 'open' | 'closed' | 'total' | 'pct') => (storeSortKey === key ? (storeSortDir === 'asc' ? '▲' : '▼') : '');
              return (
                <div className="space-y-6">
                  {jcError && (
                    <div className="p-3 rounded-md bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">{jcError}</div>
                  )}
                  {/* Row 1: Global Summary */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <InfoCard
                      label="Total"
                      gradient="from-sky-500 to-blue-600"
                      icon={
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h18v4H3zM3 9h18v12H3z" />
                        </svg>
                      }
                      value={
                        <div className="flex flex-col">
                          <span className="text-4xl font-bold">{jobCardSummary.total}</span>
                          <span className="text-white/80 text-xs">Completion {getCompletionPct(jobCardSummary.closed, jobCardSummary.total)}%</span>
                        </div>
                      }
                    />
                    <InfoCard
                      label="Open"
                      gradient="from-amber-500 to-yellow-500"
                      icon={
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
                        </svg>
                      }
                      value={
                        <div className="flex flex-col">
                          <span className="text-4xl font-bold">{jobCardSummary.open}</span>
                          <span className="text-white/80 text-xs">Remaining {100 - getCompletionPct(jobCardSummary.closed, jobCardSummary.total)}%</span>
                        </div>
                      }
                    />
                    <InfoCard
                      label="Closed"
                      gradient="from-emerald-600 to-teal-400"
                      icon={
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      }
                      value={
                        <div className="flex flex-col">
                          <span className="text-4xl font-bold">{jobCardSummary.closed}</span>
                          <span className="text-white/80 text-xs">Completion {getCompletionPct(jobCardSummary.closed, jobCardSummary.total)}%</span>
                        </div>
                      }
                    />
                  </div>

                  {isAdmin && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setIsRegionalCoachesOpen(v => !v)}
                        className="w-full mb-2 flex items-center justify-between px-3 py-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                      >
                        <span className="flex items-center gap-2">
                          <span className="text-lg font-medium">Regional Coaches</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600">{regionalCoaches.length}</span>
                          {selectedRegionalCoach && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 truncate max-w-[8rem] sm:max-w-[12rem]">{selectedRegionalCoach.name}</span>
                          )}
                        </span>
                        <span className="text-xs opacity-80">{isRegionalCoachesOpen ? 'Hide' : 'Show'}</span>
                      </button>
                      <div className={`${isRegionalCoachesOpen ? 'block' : 'hidden'}`}>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Click a Regional Coach card to filter Area Coaches and the stores table below.</p>
                        {!jcLoading && jcError === null && regionalCoaches.length === 0 && (
                          <div className="mb-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40 border border-dashed border-gray-200 dark:border-gray-700 rounded-md p-3">No Regional Coaches found.</div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {regionalCoaches.map(rc => (
                            <button
                              type="button"
                              key={rc.id}
                              onClick={() => {
                                if (selectedRegionalCoach?.id === rc.id) {
                                  setSelectedRegionalCoach(null);
                                } else {
                                  setSelectedRegionalCoach({ id: rc.id, name: rc.name });
                                  setSelectedAreaCoach(null);
                                  setIsAreaCoachesOpen(true);
                                  setIsRegionalCoachesOpen(false);
                                }
                              }}
                              className={`text-left shadow-lg rounded-xl p-5 bg-white dark:bg-gray-800 border ${selectedRegionalCoach?.id === rc.id ? 'border-blue-500' : 'border-gray-100 dark:border-gray-700'} transform transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl`}
                            >
                              <div className="flex items-center justify-between mb-3">
                                <div className="text-base font-semibold text-gray-800 dark:text-gray-200">{rc.name}</div>
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M3 12h18M3 17h18" />
                                </svg>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-sky-500 to-blue-600">
                                  <span className="opacity-90">Total</span>
                                  <span className="text-white/90">{rc.summary.total}</span>
                                </span>
                                <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500">
                                  <span className="opacity-90">Open</span>
                                  <span className="text-white/90">{rc.summary.open}</span>
                                </span>
                                <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-emerald-600 to-teal-400">
                                  <span className="opacity-90">Closed</span>
                                  <span className="text-white/90">{rc.summary.closed}</span>
                                </span>
                                <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-slate-500 to-slate-600">
                                  <span className="opacity-90">Completion</span>
                                  <span className="text-white/90">{getCompletionPct(rc.summary.closed, rc.summary.total)}%</span>
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Row 2: Area Coaches (regional only) */}
                  {(isRegionalCoach || isAdmin) && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setIsAreaCoachesOpen(v => !v)}
                        className="w-full mb-2 flex items-center justify-between px-3 py-2 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                      >
                        <span className="flex items-center gap-2">
                          <span className="text-lg font-medium">Area Coaches</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600">{areaCoaches.length}</span>
                          {selectedAreaCoach && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 truncate max-w-[8rem] sm:max-w-[12rem]">{selectedAreaCoach.name}</span>
                          )}
                        </span>
                        <span className="text-xs opacity-80">{isAreaCoachesOpen ? 'Hide' : 'Show'}</span>
                      </button>
                      <div className={`${isAreaCoachesOpen ? 'block' : 'hidden'}`}>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Click an Area Coach card to filter the stores table below.</p>
                        {!jcLoading && jcError === null && areaCoaches.length === 0 && (
                          <div className="mb-3 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40 border border-dashed border-gray-200 dark:border-gray-700 rounded-md p-3">No Area Coaches found.</div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {areaCoaches.map(ac => (
                            <button
                              type="button"
                              key={ac.id}
                              onClick={() => {
                                if (selectedAreaCoach?.id === ac.id) {
                                  setSelectedAreaCoach(null);
                                } else {
                                  setSelectedAreaCoach({ id: ac.id, name: ac.name });
                                  setIsAreaCoachesOpen(false);
                                }
                              }}
                              className={`text-left shadow-lg rounded-xl p-5 bg-white dark:bg-gray-800 border ${selectedAreaCoach?.id === ac.id ? 'border-blue-500' : 'border-gray-100 dark:border-gray-700'} transform transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl`}
                            >
                              <div className="flex items-center justify-between mb-3">
                                <div className="text-base font-semibold text-gray-800 dark:text-gray-200">{ac.name}</div>
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M3 12h18M3 17h18" />
                                </svg>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-sky-500 to-blue-600">
                                  <span className="opacity-90">Total</span>
                                  <span className="text-white/90">{ac.summary.total}</span>
                                </span>
                                <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500">
                                  <span className="opacity-90">Open</span>
                                  <span className="text-white/90">{ac.summary.open}</span>
                                </span>
                                <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-emerald-600 to-teal-400">
                                  <span className="opacity-90">Closed</span>
                                  <span className="text-white/90">{ac.summary.closed}</span>
                                </span>
                                <span className="inline-flex items-center gap-2 text-white text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-slate-500 to-slate-600">
                                  <span className="opacity-90">Completion</span>
                                  <span className="text-white/90">{getCompletionPct(ac.summary.closed, ac.summary.total)}%</span>
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Selected AC pill and clear filter */}
                  {(selectedAreaCoach || selectedRegionalCoach) && (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="inline-flex flex-wrap items-center gap-2 text-xs sm:text-sm px-3 py-1 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 max-w-full">
                        <span>Filtering by:</span>
                        {selectedRegionalCoach && (
                          <>
                            <span className="opacity-80">RC</span>
                            <strong className="truncate max-w-[10rem] sm:max-w-none">{selectedRegionalCoach.name}</strong>
                          </>
                        )}
                        {selectedAreaCoach && (
                          <>
                            <span className="opacity-60">•</span>
                            <span className="opacity-80">AC</span>
                            <strong className="truncate max-w-[10rem] sm:max-w-none">{selectedAreaCoach.name}</strong>
                          </>
                        )}
                      </div>
                      <button
                        className="text-xs sm:text-sm px-3 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 w-full sm:w-auto"
                        onClick={() => {
                          setSelectedAreaCoach(null);
                          setSelectedRegionalCoach(null);
                        }}
                      >
                        Clear Filter
                      </button>
                    </div>
                  )}

                  {/* Table: Per-store breakdown */}
                  <div className="mt-2 overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-200">Store Job Cards</div>
                      <button
                        className="text-sm px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 w-full sm:w-auto"
                        onClick={() => exportStoresToCsv(sortedStoreRows)}
                      >
                        Export CSV
                      </button>
                    </div>
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-900/40">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => onSort('name')}>Store <span className="ml-1">{sortArrow('name')}</span></th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => onSort('open')}>Open <span className="ml-1">{sortArrow('open')}</span></th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => onSort('closed')}>Closed <span className="ml-1">{sortArrow('closed')}</span></th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => onSort('total')}>Total <span className="ml-1">{sortArrow('total')}</span></th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => onSort('pct')}>Completion % <span className="ml-1">{sortArrow('pct')}</span></th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">View</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {sortedStoreRows.map((row: { id: number; name: string; storenumber?: string; store_email?: string; open: number; closed: number; total: number }) => {
                          const total = row.open + row.closed;
                          const pct = getCompletionPct(row.closed, total);
                          return (
                            <tr key={row.id}>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">{row.name}{row.storenumber ? ` - ${row.storenumber}` : ''}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-amber-600 dark:text-amber-400">{row.open}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-emerald-600 dark:text-emerald-400">{row.closed}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-blue-600 dark:text-blue-400">{total}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">{pct}%</td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm">
                                <button
                                  title="View store job cards"
                                  className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                  onClick={() => navigateToStoreUserJobCards(row.store_email, row.id)}
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {jcLoading && (
                    <div className="pt-3"><LoadingSpinner size="md" variant="dark" label="Loading job cards..." /></div>
                  )}
                </div>
              );
            })()}
          </div>
        </>
    );
}

  // Lightweight presentational card used by the Job Cards Overview summary row
  // Accepts a gradient for visual distinction and an optional icon to align with the metric label
  const InfoCard = ({
    label,
    value,
    gradient,
    icon,
  }: {
    label: string;
    value: ReactNode;
    gradient: string; // tailwind class e.g., "from-indigo-500 to-purple-600"
    icon?: ReactNode;
  }) => (
    <div className={`shadow-lg rounded-xl p-6 text-white bg-gradient-to-br ${gradient} transform transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl`}>
      <div className="flex flex-col">
        <div className="flex items-center gap-2 text-white/70 mb-1">
          {icon}
          <span className="text-base">{label}</span>
        </div>
        <div className="font-bold text-4xl leading-tight min-h-[2.5rem] flex items-center">{value}</div>
      </div>
    </div>
  );

  // Generic spinner used for inline loading states inside the overview section
  const LoadingSpinner = ({
    size = 'md',
    label,
    variant = 'light',
  }: {
    size?: 'sm' | 'md' | 'lg';
    label?: string;
    variant?: 'light' | 'dark';
  }) => {
    const dimension = size === 'sm' ? 16 : size === 'lg' ? 36 : 24;
    const ringBase = variant === 'light' ? 'border-white/20 border-t-white' : 'border-gray-300 border-t-blue-500';
    const textClass = variant === 'light' ? 'text-white/80' : 'text-gray-600';
    return (
      <div className="flex items-center gap-2">
        <span
          className={`inline-block rounded-full ${ringBase} animate-spin`}
          style={{ width: dimension, height: dimension, borderWidth: Math.max(2, Math.floor(dimension / 8)) }}
        />
        {label && <span className={`text-sm ${textClass}`}>{label}</span>}
      </div>
    );
  };

interface DashboardCardProps {
    title: string;
    value: string;
    icon: React.ReactNode;
    footer: string;
    variant?: 'blue' | 'yellow' | 'green' | 'purple';
    onClick?: () => void;
}

function DashboardCard({ title, value, icon, footer, variant = 'blue', onClick }: DashboardCardProps) {
    const variants = {
        blue: 'bg-blue-100/80 dark:bg-blue-900/50 border border-blue-200 dark:border-blue-800',
        yellow: 'bg-yellow-100/80 dark:bg-yellow-900/50 border border-yellow-200 dark:border-yellow-800',
        green: 'bg-green-100/80 dark:bg-green-900/50 border border-green-200 dark:border-green-800',
        purple: 'bg-purple-100/80 dark:bg-purple-900/50 border border-purple-200 dark:border-purple-800'
    };

    return (
        <div 
            className={`rounded-lg shadow-sm p-6 hover:shadow-md transition-all ${variants[variant]} ${onClick ? 'cursor-pointer hover:scale-105' : ''}`}
            onClick={onClick}
        >
            <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{title}</p>
                {icon}
            </div>
            <p className="text-3xl font-semibold text-gray-900 dark:text-white mb-4">{value}</p>
            <p className="text-sm text-gray-700 dark:text-gray-300">{footer}</p>
        </div>
    );
}